import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { RequireRole } from '@/components/RequireRole'
import { RequireClient } from '@/components/RequireClient'
import { SessionProvider } from '@/hooks/useSession'
import { AdminLayout } from '@/components/admin/AdminLayout'
import { NotificacoesLayout } from '@/routes/operations/NotificacoesLayout'
import { OperationsLayout } from '@/routes/operations/OperationsLayout'
import { PeriodProvider } from '@/contexts/PeriodContext'
import { Login } from '@/routes/Login'
import { SsoCallback } from '@/routes/SsoCallback'
import { NotFound } from '@/routes/NotFound'

// Each route is code-split into its own chunk that loads on first navigation,
// so the initial bundle no longer carries every screen (and their heavy deps —
// chart.js, etc.) up front. Eager on the first-paint path, and therefore NOT
// lazied: the layout shells (OperationsLayout / AdminLayout), the auth guard,
// Login, and NotFound. The <Suspense> boundaries that catch these lazy chunks
// live around the <Outlet> in each layout, so the sidebar / admin tabs stay
// put while a page chunk is in flight.
const VisaoGlobal = lazy(() =>
  import('@/routes/operations/VisaoGlobal').then((m) => ({ default: m.VisaoGlobal })),
)
const VisaoGeral = lazy(() =>
  import('@/routes/operations/VisaoGeral').then((m) => ({ default: m.VisaoGeral })),
)
// Chaves de API pessoais. Fica em /operacoes e NAO em /admin: esse ramo e guardado a
// ['admin','team_lead'], e a decisao e que qualquer membro activo cria as suas proprias chaves —
// um consultor incluido. E o mesmo erro que o /portal/definicoes comete do outro lado.
const AdminApiMcp = lazy(() =>
  import('@/routes/admin/ApiMcp').then((m) => ({ default: m.AdminApiMcp })),
)
const ChavesApi = lazy(() =>
  import('@/routes/operations/ChavesApi').then((m) => ({ default: m.ChavesApi })),
)
const OperationsRisco = lazy(() =>
  import('@/routes/operations/Risco').then((m) => ({ default: m.Risco })),
)
const RiscoClientDetail = lazy(() =>
  import('@/routes/operations/RiscoClientDetail').then((m) => ({ default: m.RiscoClientDetail })),
)
const MeusClientes = lazy(() =>
  import('@/routes/operations/MeusClientes').then((m) => ({ default: m.MeusClientes })),
)
const OperationsClientDetail = lazy(() =>
  import('@/routes/operations/ClientDetail').then((m) => ({ default: m.ClientDetail })),
)
const ClientDetailLayout = lazy(() =>
  import('@/routes/operations/client-detail/ClientDetailLayout').then((m) => ({
    default: m.ClientDetailLayout,
  })),
)
const ClientAnaliticaTab = lazy(() =>
  import('@/routes/operations/client-detail/AnaliticaTab').then((m) => ({
    default: m.AnaliticaTab,
  })),
)
const ClientReconciliacaoAtTab = lazy(() =>
  import('@/routes/operations/client-detail/ReconciliacaoAtTab').then((m) => ({
    default: m.ReconciliacaoAtTab,
  })),
)
const ClientRegrasTab = lazy(() =>
  import('@/routes/operations/client-detail/RegrasTab').then((m) => ({ default: m.RegrasTab })),
)
const ClientEtiquetasTab = lazy(() =>
  import('@/routes/operations/client-detail/EtiquetasTab').then((m) => ({
    default: m.EtiquetasTab,
  })),
)
const OperationsReconciliacao = lazy(() =>
  import('@/routes/operations/Reconciliacao').then((m) => ({ default: m.Reconciliacao })),
)
const OperationsReconciliacaoOverview = lazy(() =>
  import('@/routes/operations/ReconciliacaoOverview').then((m) => ({
    default: m.ReconciliacaoOverview,
  })),
)
const OperationsReconciliacaoCliente = lazy(() =>
  import('@/routes/operations/ReconciliacaoCliente').then((m) => ({
    default: m.ReconciliacaoCliente,
  })),
)
const OperationsEFatura = lazy(() =>
  import('@/routes/operations/EFatura').then((m) => ({ default: m.EFatura })),
)
const OperationsEFaturaIndex = lazy(() =>
  import('@/routes/operations/EFaturaIndex').then((m) => ({ default: m.EFaturaIndex })),
)
const OperationsExtratos = lazy(() =>
  import('@/routes/operations/Extratos').then((m) => ({ default: m.Extratos })),
)
const PortalLayout = lazy(() =>
  import('@/routes/portal/PortalLayout').then((m) => ({ default: m.PortalLayout })),
)
const ClientHomePage = lazy(() =>
  import('@/routes/portal/ClientHomePage').then((m) => ({ default: m.ClientHomePage })),
)
const ClientModulesPage = lazy(() =>
  import('@/routes/portal/ClientModulesPage').then((m) => ({ default: m.ClientModulesPage })),
)
const ClientSuppliersPage = lazy(() =>
  import('@/routes/portal/ClientSuppliersPage').then((m) => ({ default: m.ClientSuppliersPage })),
)
const ClientDocumentsPage = lazy(() =>
  import('@/routes/portal/ClientDocumentsPage').then((m) => ({ default: m.ClientDocumentsPage })),
)
const ClientActivityPage = lazy(() =>
  import('@/routes/portal/ClientActivityPage').then((m) => ({ default: m.ClientActivityPage })),
)
const ClientUsersPage = lazy(() =>
  import('@/routes/portal/ClientUsersPage').then((m) => ({ default: m.ClientUsersPage })),
)
const ClientBankAccountsPage = lazy(() =>
  import('@/routes/portal/ClientBankAccountsPage').then((m) => ({
    default: m.ClientBankAccountsPage,
  })),
)
const ClientEFaturaPage = lazy(() =>
  import('@/routes/portal/ClientEFaturaPage').then((m) => ({ default: m.ClientEFaturaPage })),
)
const ClientNovidadesPage = lazy(() =>
  import('@/routes/portal/ClientNovidadesPage').then((m) => ({ default: m.ClientNovidadesPage })),
)
const ClientReconciliationPage = lazy(() =>
  import('@/routes/portal/ClientReconciliationPage').then((m) => ({
    default: m.ClientReconciliationPage,
  })),
)
const ClientTagSettingsPage = lazy(() =>
  import('@/routes/portal/ClientTagSettingsPage').then((m) => ({
    default: m.ClientTagSettingsPage,
  })),
)
const ClientMarkerSettingsPage = lazy(() =>
  import('@/routes/portal/ClientMarkerSettingsPage').then((m) => ({
    default: m.ClientMarkerSettingsPage,
  })),
)
const PortalDefinicoesLayout = lazy(() =>
  import('@/routes/portal/PortalDefinicoesLayout').then((m) => ({
    default: m.PortalDefinicoesLayout,
  })),
)
const PortalDefinicoesIndex = lazy(() =>
  import('@/routes/portal/PortalDefinicoesLayout').then((m) => ({
    default: m.DefinicoesIndexRedirect,
  })),
)
const OperationsIva = lazy(() =>
  import('@/routes/operations/Iva').then((m) => ({ default: m.Iva })),
)
const OperationsIvaCliente = lazy(() =>
  import('@/routes/operations/IvaCliente').then((m) => ({ default: m.IvaCliente })),
)
const Documentos = lazy(() =>
  import('@/routes/operations/Documentos').then((m) => ({ default: m.Documentos })),
)
const RevisaoPendentes = lazy(() =>
  import('@/routes/operations/RevisaoPendentes').then((m) => ({ default: m.RevisaoPendentes })),
)
const Notificacoes = lazy(() =>
  import('@/routes/operations/Notificacoes').then((m) => ({ default: m.Notificacoes })),
)
const Revisoes = lazy(() =>
  import('@/routes/operations/Revisoes').then((m) => ({ default: m.Revisoes })),
)
const Remetentes = lazy(() =>
  import('@/routes/operations/Remetentes').then((m) => ({ default: m.Remetentes })),
)
const MinhasCorrecoes = lazy(() =>
  import('@/routes/operations/MinhasCorrecoes').then((m) => ({ default: m.MinhasCorrecoes })),
)
const AdminSupplierDuplicates = lazy(() =>
  import('@/routes/admin/SupplierDuplicates').then((m) => ({ default: m.AdminSupplierDuplicates })),
)
const AdminSuppliers = lazy(() =>
  import('@/routes/admin/Suppliers').then((m) => ({ default: m.AdminSuppliers })),
)
const AdminOnboarding = lazy(() =>
  import('@/routes/admin/Onboarding').then((m) => ({ default: m.AdminOnboarding })),
)
const AdminClients = lazy(() =>
  import('@/routes/admin/Clients').then((m) => ({ default: m.AdminClients })),
)
const AdminClientDetail = lazy(() =>
  import('@/routes/admin/ClientDetailAdmin').then((m) => ({ default: m.ClientDetailAdmin })),
)
const AdminConsultants = lazy(() =>
  import('@/routes/admin/Consultants').then((m) => ({ default: m.AdminConsultants })),
)
const AdminRisco = lazy(() =>
  import('@/routes/admin/Risco').then((m) => ({ default: m.AdminRisco })),
)
const AdminNotificacoes = lazy(() =>
  import('@/routes/admin/Notificacoes').then((m) => ({ default: m.AdminNotificacoes })),
)
const AdminAnalytics = lazy(() =>
  import('@/routes/admin/Analytics').then((m) => ({ default: m.Analytics })),
)
const AdminContabilidade = lazy(() =>
  import('@/routes/admin/Contabilidade').then((m) => ({ default: m.AdminContabilidade })),
)
const AdminRoberto = lazy(() =>
  import('@/routes/admin/RobertoAnalytics').then((m) => ({ default: m.RobertoAnalytics })),
)
const AdminNovidades = lazy(() =>
  import('@/routes/admin/Novidades').then((m) => ({ default: m.AdminNovidades })),
)
const AdminLearning = lazy(() =>
  import('@/routes/admin/Learning').then((m) => ({ default: m.Learning })),
)
const AdminClientUsers = lazy(() =>
  import('@/routes/admin/ClientUsers').then((m) => ({ default: m.AdminClientUsers })),
)
const AdminFirms = lazy(() =>
  import('@/routes/admin/Firms').then((m) => ({ default: m.AdminFirms })),
)

// /operacoes/* and /admin/* both render under OperationsLayout — the sidebar
// is the platform-wide shell. Admin pages stack a tabbed sub-nav inside the
// layout's <Outlet>.

// Forwarding redirect — preserves `?bugger` (and any other sticky params)
// across the / → /operacoes hop so a refresh from the bookmark keeps the
// debug widget alive. See apps/web/src/lib/stickyParams.ts.
function IndexRedirect() {
  const location = useLocation()
  return (
    <Navigate
      to={{ pathname: '/operacoes', search: location.search, hash: location.hash }}
      replace
    />
  )
}

export function App() {
  return (
    <BrowserRouter>
      {/* One resolver for the whole app. Every guard and layout used to call useSession()
          independently, so each mount re-ran the principal lookup and the provisioning round-trip. */}
      <SessionProvider>
        <Routes>
          <Route path="/" element={<IndexRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/sso" element={<SsoCallback />} />

          {/* External CLIENT portal — read-only, RLS-scoped. Separate from the staff shell. The layout
            owns the brand + company switcher + top nav (Documentos · Utilizadores); pages render in its
            Outlet and read the selected company via usePortal(). */}
          <Route
            path="/portal"
            element={
              <RequireClient>
                {(client) => (
                  <Suspense fallback={null}>
                    <PortalLayout client={client} />
                  </Suspense>
                )}
              </RequireClient>
            }
          >
            {/* Início is the landing page (Diogo, 2026-09-14). Módulos stays one click away in
                the nav as an explainer rather than a gate the client passes every morning.
                The redirect deliberately keeps no query string — PortalLayout's useEmpresaUrlSync
                puts `?empresa=` back, and hardcoding it here would fight that repair. */}
            <Route index element={<Navigate to="/portal/inicio" replace />} />
            <Route path="inicio" element={<ClientHomePage />} />
            {/* Novidades — ungated, like Início: everyone who logs in gets told what changed.
                RLS shows only PUBLISHED entries of a firm they are a client of. */}
            <Route path="novidades" element={<ClientNovidadesPage />} />
            <Route path="modulos" element={<ClientModulesPage />} />
            <Route path="fornecedores" element={<ClientSuppliersPage />} />
            <Route path="documentos" element={<ClientDocumentsPage />} />
            {/* "O que aconteceu ao que enviei" — same documents, ordered by arrival. A sibling
                route rather than a child of documentos: ClientDocumentsPage owns a lot of URL
                state and re-parenting it under a layout just to gain a tab bar risks all of it. */}
            <Route path="documentos/atividade" element={<ClientActivityPage />} />
            <Route path="utilizadores" element={<ClientUsersPage />} />
            <Route path="contas-bancarias" element={<ClientBankAccountsPage />} />
            <Route path="efatura" element={<ClientEFaturaPage />} />
            {/* Reached from the e-Fatura page, which is admin-gated; the page itself is scoped by
                RLS regardless of how it is reached. */}
            <Route path="reconciliacao" element={<ClientReconciliationPage />} />
            <Route path="definicoes" element={<PortalDefinicoesLayout />}>
              <Route index element={<PortalDefinicoesIndex />} />
              <Route path="analitica" element={<ClientTagSettingsPage />} />
              <Route path="etiquetas" element={<ClientMarkerSettingsPage />} />
              {/* 2026-09-03 terminology swap: the simple labels are now "Etiquetas".
                  Old marker links keep working; the old /etiquetas path deliberately
                  has NO redirect — it is the new markers page (see AGENTS.md gotcha). */}
              <Route path="marcadores" element={<Navigate to="../etiquetas" replace />} />
            </Route>
          </Route>

          {/* "Rever pendentes" — the full-screen triage flow. Sits OUTSIDE OperationsLayout on
              purpose: it is a focus mode with no app sidebar, so the operator's whole screen is
              the document and the decision. Same role guard as the rest of /operacoes.
              It carries its OWN PeriodProvider because the shared one belongs to OperationsLayout:
              without it `usePeriod` falls back to component-local state seeded with the CURRENT
              MONTH (PeriodContext.tsx) rather than the persisted `jca.period`, and the queue would
              silently scope to a different window than the card the operator just clicked. The
              provider initialises from that same localStorage key, so the period carries over. */}
          <Route
            path="/operacoes/documentos/rever"
            element={
              <RequireRole roles={['admin', 'team_lead', 'consultor']}>
                <Suspense fallback={null}>
                  <PeriodProvider>
                    <RevisaoPendentes />
                  </PeriodProvider>
                </Suspense>
              </RequireRole>
            }
          />

          <Route
            element={
              // consultor sees all of /operacoes/* (own carteira, RLS-scoped);
              // the /admin subtree keeps its own admin+team_lead guard below.
              <RequireRole roles={['admin', 'team_lead', 'consultor']}>
                <OperationsLayout />
              </RequireRole>
            }
          >
            <Route path="/operacoes">
              {/* /operacoes/analytics is the canonical Analytics route
                (#28: VisaoGlobal renamed). The bare /operacoes redirects
                there so old bookmarks don't break and the sidebar lights
                up correctly.
                /operacoes/visao-geral hosts the restored firm-wide
                dashboard (#27). */}
              <Route index element={<Navigate to="/operacoes/analytics" replace />} />
              <Route path="analytics" element={<VisaoGlobal />} />
              <Route path="visao-geral" element={<VisaoGeral />} />
              <Route path="clientes" element={<MeusClientes />} />
              <Route path="carteira" element={<Navigate to="/operacoes/clientes" replace />} />
              <Route path="cliente/:clientId" element={<ClientDetailLayout />}>
                <Route index element={<OperationsClientDetail />} />
                <Route path="analitica" element={<ClientAnaliticaTab />} />
                <Route path="regras" element={<ClientRegrasTab />} />
                <Route path="etiquetas" element={<ClientEtiquetasTab />} />
                <Route path="reconciliacao-at" element={<ClientReconciliacaoAtTab />} />
              </Route>
              <Route path="extratos" element={<OperationsExtratos />} />
              {/* Índice de empresas → os documentos que compõem o apuramento dessa empresa,
                  com a classificação contabilística a fazer-se ali. O MESMO gesto da
                  reconciliação bancária logo abaixo, de propósito (Diogo, 2026-10-29). */}
              <Route path="iva" element={<OperationsIva />} />
              <Route path="iva/cliente/:clientId" element={<OperationsIvaCliente />} />
              {/* Companies index → the company's bank accounts → the pairing
                  surface. Reconciling is a deliberate act ("Associar agora"),
                  not something you fall into by opening a company. */}
              <Route path="reconciliacao" element={<OperationsReconciliacaoOverview />} />
              <Route
                path="reconciliacao/cliente/:clientId"
                element={<OperationsReconciliacaoCliente />}
              />
              <Route
                path="reconciliacao/cliente/:clientId/associar"
                element={<OperationsReconciliacao />}
              />
              <Route path="efatura" element={<OperationsEFaturaIndex />} />
              <Route path="efatura/cliente/:clientId" element={<OperationsEFatura />} />
              <Route path="risco" element={<OperationsRisco />} />
              <Route path="risco/cliente/:clientId" element={<RiscoClientDetail />} />
              <Route path="documentos" element={<Documentos />} />
              <Route path="chaves-api" element={<ChavesApi />} />
              <Route path="notificacoes" element={<NotificacoesLayout />}>
                <Route index element={<Notificacoes />} />
                {/* Remetentes and Revisões are team-lead/admin queues. They were only HIDDEN
                    from the tab bar for a consultor, never guarded — a consultor who deep-linked
                    (or followed the legacy /operacoes/revisoes redirect below) rendered both.
                    Revisões then showed the proposal queue, which until migration
                    20260920234500 was not even firm-scoped. Hiding is not a permission. */}
                <Route
                  path="remetentes"
                  element={
                    <RequireRole roles={['admin', 'team_lead']}>
                      <Remetentes />
                    </RequireRole>
                  }
                />
                <Route
                  path="revisoes"
                  element={
                    <RequireRole roles={['admin', 'team_lead']}>
                      <Revisoes />
                    </RequireRole>
                  }
                />
                <Route path="minhas-correcoes" element={<MinhasCorrecoes />} />
              </Route>
              {/* #96 — Revisões/Minhas Correções are now tabs under Notificações;
                redirect the old flat paths so existing deep-links keep working. */}
              <Route
                path="revisoes"
                element={<Navigate to="/operacoes/notificacoes/revisoes" replace />}
              />
              <Route
                path="minhas-correcoes"
                element={<Navigate to="/operacoes/notificacoes/minhas-correcoes" replace />}
              />
            </Route>

            <Route
              path="/admin"
              element={
                <RequireRole roles={['admin', 'team_lead']}>
                  <AdminLayout />
                </RequireRole>
              }
            >
              {/* The Resumo landing tab is gone — /admin lands on Clientes (its lists became
                filterable columns there; platform settings moved to Notificações). */}
              <Route index element={<Navigate to="/admin/clients" replace />} />
              {/* Firm-scoped, not platform-only: f_analytics_* now filter by current_firm_id(), so
                each firm's admin sees their own AI-cost and throughput data. It was platformOnly
                only because those RPCs pinned themselves to the JA firm's uuid. */}
              <Route
                path="analytics"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminAnalytics />
                  </RequireRole>
                }
              />
              {/* API & MCP: instrucoes, as chaves da propria pessoa, e a auditoria das chaves da
                firma. A guarda de papel esta aqui E no servidor (f_api_keys_firm levanta
                `forbidden`) — esconder um separador nao e um portao. */}
              <Route
                path="api"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminApiMcp />
                  </RequireRole>
                }
              />
              {/* Roberto's interaction log. Admin-only for a different reason than
                Analytics: not because it is cross-firm, but because it holds the
                clients' own questions and the supplier names and amounts in the
                answers. The RPCs enforce it again server-side. */}
              <Route
                path="roberto"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminRoberto />
                  </RequireRole>
                }
              />
              {/* Correlação fornecedor ↔ classificação contabilística — reporting admin-only;
                a RPC definer volta a impor o papel e o firm scope no servidor. */}
              <Route
                path="contabilidade"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminContabilidade />
                  </RequireRole>
                }
              />
              {/* Novidades authoring. Admin-only: publishing speaks to every client of the
                firm at once, which is not a thing a consultor should be able to do. The four
                RPCs enforce it again server-side. */}
              <Route
                path="novidades"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminNovidades />
                  </RequireRole>
                }
              />
              {/* The platform operator's tenant control plane — genuinely cross-firm, so this is
                what platformOnly is for now that it means platform_admins. */}
              <Route
                path="escritorios"
                element={
                  <RequireRole roles={['admin']} platformOnly>
                    <AdminFirms />
                  </RequireRole>
                }
              />
              <Route path="carteiras" element={<Navigate to="/admin/clients" replace />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="onboarding" element={<AdminOnboarding />} />
              {/* SAFT-AI-style client subpage: clicking a client opens this instead of the old
                edit dialog (view tabs + inline Editar). */}
              <Route path="clients/:clientId" element={<AdminClientDetail />} />
              <Route
                path="fornecedores"
                element={
                  <RequireRole roles={['admin', 'team_lead']}>
                    <AdminSuppliers />
                  </RequireRole>
                }
              />
              <Route
                path="fornecedores/duplicados"
                element={
                  <RequireRole roles={['admin', 'team_lead']}>
                    <AdminSupplierDuplicates />
                  </RequireRole>
                }
              />
              <Route
                path="utilizadores"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminClientUsers />
                  </RequireRole>
                }
              />
              <Route
                path="consultants"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminConsultants />
                  </RequireRole>
                }
              />
              <Route
                path="risco"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminRisco />
                  </RequireRole>
                }
              />
              <Route
                path="learning"
                element={
                  <RequireRole roles={['admin', 'team_lead']}>
                    <AdminLearning />
                  </RequireRole>
                }
              />
              <Route
                path="notificacoes"
                element={
                  <RequireRole roles={['admin']}>
                    <AdminNotificacoes />
                  </RequireRole>
                }
              />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  )
}
